import fscreen from 'fscreen';
import get from 'lodash/get';
import { takeLatest, all, call, put, select, takeEvery, fork, race, take } from 'redux-saga/effects';
import { loadDeviation, togglePanel, showPanel, hidePanel, requestChangeViewMode, fullscreenExited, fullscreenEntered, initDeviationPage, enterZoomMode, exitZoomMode, expandAndFocusCommentPanel } from './actions';
import { initializeThreadFromPapi, initializeEmptyThread } from '../../CommentThread/actionCreators';
import { focusEditorWithScroll } from '../../CommentEditor/redux/actionCreators';
import { normalize } from 'normalizr';
import { deviationSchema, getDeviationEntityId } from '../../redux/normalizr/schemas/deviation';
import { putEntities } from '../../redux/entities/actions';
import { actions as collectionActions } from '../../CollectionPanel/redux';
import { getVisiblePanel, getIsTheaterForced, getIsFullscreenAllowed, getBiEventData } from './selectors';
import { getIsLoggedIn, getViewMode, getIsMobile } from '../../publicSession/selectors';
import { restrictLoggedOut } from '../../redux/loggedout';
import ddt from '@wix/da-ddt';
import { DeviationViewMode } from '@wix/da-react-context/pkg/SessionContext';
import { setViewMode } from '../../publicSession/actions';
import { ReferralType, BiEventType } from '@wix/da-bi/pkg/constants';
import biLogger from '@wix/da-bi/pkg/BiLogger.logger';
import { getBiEventTypeForViewChange, getDeviationNotificationQueue, getCommentableTypeForDeviationType } from './helpers';
import { setWatchingState } from '../../WatchButton/redux/actionCreators';
import { exitDuperbrowse, shutdownDuperbrowse, unloadDuperbrowseItem } from '../../Duperbrowse/redux/actionCreators';
import { logPageView } from '../../biLogger/redux/actions';
import { collectFave } from '../../CollectionPanel/redux/actionCreators';
import { removeQueues } from '../../Notifications/redux/actionCreators';
import { getCommentBoxId } from '../../CommentThread/helpers';
import { pushPopover } from '../../Popovers/redux/actionCreators';
import { PopoverType } from '../../Popovers/redux/types';
import { isDeviation } from '../../Duperbrowse/redux/helpers';
import { queueSafetyChangeForDeviation, recordDeviationSafety, setPageSafety } from '../../Ads/redux/actions';
import { getDeviationSafety, getQueuedDeviationId } from '../../Ads/redux/selectors';
import { getCurrentBiMetadata } from '../../Duperbrowse/redux/selectors';
const fsLogger = ddt.createLogger('fullscreen');
function* handleLoadDeviation({ payload: { deviation } }) {
    // Ad safety
    const deviationSafety = yield select(getDeviationSafety);
    const newSafety = deviationSafety[getDeviationEntityId(deviation)];
    if (newSafety) {
        ddt.log('ads', 'Setting new page safety', newSafety);
        yield put(setPageSafety({
            safety: newSafety
        }));
    } else {
        // As we don't know the full page safety, set based on what we do know
        // the queued change can then handle sidebar etc items
        if (deviation.isMature || deviation.isNsfg) {
            ddt.log('ads', 'Setting new page safety fallback', deviation.deviationId);
            yield put(setPageSafety({
                safety: 'off'
            }));
        }
        yield put(queueSafetyChangeForDeviation({
            deviation
        }));
        ddt.log('ads', 'Queue safety change', deviation.deviationId, deviationSafety);
    }
    const biMetadata = yield select(getCurrentBiMetadata, deviation.deviationId);
    const { view, eventComponent, deviationId, deviationResourceType, isDuperbrowse, contentViewStatus } = yield select(getBiEventData, deviation);
    yield put(logPageView({
        view,
        component: eventComponent,
        typeid: deviationResourceType,
        itemid: deviationId,
        is_duper: isDuperbrowse ? 1 : 0,
        content_view_status: contentViewStatus,
        metadata: biMetadata,
        url: deviation.url,
        window_url: window.location.toString()
    }));
}
function* handleInitDeviationPage({ payload: { deviation, comments, safety } }) {
    const { entities } = normalize(deviation, deviationSchema);
    const isAuthorWatched = get(deviation, 'extended.isWatchingAuthor', false);
    const author = get(deviation, 'author.username', '');
    const actions = [
        put(comments ? initializeThreadFromPapi(comments) : initializeEmptyThread(deviation.deviationId, getCommentableTypeForDeviationType(deviation.typeId))),
        put(setWatchingState(author, isAuthorWatched)),
        put(putEntities({
            entities
        })),
        put(recordDeviationSafety({
            deviation,
            safety
        }))
    ];
    // Ad safety race fallback, deals with load then fetch situation
    const queuedAdSafetyId = yield select(getQueuedDeviationId);
    if (queuedAdSafetyId && queuedAdSafetyId === getDeviationEntityId(deviation)) {
        ddt.log('ads', 'Setting new page safety', safety);
        actions.push(put(setPageSafety({
            safety
        })));
    }
    yield all(actions);
}
function* handleUnloadDuperbrowseItem({ payload: { deviation } }) {
    if (!deviation || !isDeviation(deviation)) {
        return;
    }
    yield put(removeQueues((queueName)=>queueName.startsWith('deviation') && queueName !== getDeviationNotificationQueue(deviation)));
}
function* handleTogglePanel({ payload: { actionBar, panelType } }) {
    if (!panelType) {
        return;
    }
    const visible = yield select(getVisiblePanel, actionBar);
    if (visible === panelType) {
        yield put(hidePanel(panelType));
        return;
    }
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        const referrals = {
            collection: ReferralType.FAVOURITE,
            groups: ReferralType.ADD_TO_GROUP,
            comment: ReferralType.COMMENT
        };
        yield put(restrictLoggedOut({
            restrictedActionName: referrals[panelType]
        }));
        return;
    }
    yield put(showPanel(panelType, actionBar));
}
function handleEnterFullscreen() {
    fsLogger.log('Requesting fullscreen');
    fscreen.requestFullscreen(document.documentElement);
}
function handleExitFullscreen() {
    fsLogger.log('Requesting exit fullscreen');
    fscreen.exitFullscreen();
}
function* getNextViewMode(currentViewMode, requestedViewMode, deviation) {
    const isTheaterStateForced = yield select(getIsTheaterForced, deviation);
    const normalModeIfAllowed = isTheaterStateForced ? DeviationViewMode.Theater : DeviationViewMode.Normal;
    const isFullscreenAllowed = yield select(getIsFullscreenAllowed);
    let nextViewMode;
    switch(currentViewMode){
        case DeviationViewMode.Fullscreen:
            switch(requestedViewMode){
                case DeviationViewMode.Fullscreen:
                case DeviationViewMode.Normal:
                    nextViewMode = normalModeIfAllowed;
                    break;
                case DeviationViewMode.Theater:
                    nextViewMode = DeviationViewMode.Theater;
                    break;
                default:
            }
            break;
        case DeviationViewMode.Theater:
            switch(requestedViewMode){
                case DeviationViewMode.Fullscreen:
                    if (isFullscreenAllowed) {
                        nextViewMode = DeviationViewMode.Fullscreen;
                    }
                    break;
                case DeviationViewMode.Theater:
                case DeviationViewMode.Normal:
                    nextViewMode = normalModeIfAllowed;
                    break;
                default:
            }
            break;
        case DeviationViewMode.Normal:
            switch(requestedViewMode){
                case DeviationViewMode.Fullscreen:
                    if (isFullscreenAllowed) {
                        nextViewMode = DeviationViewMode.Fullscreen;
                    }
                    break;
                case DeviationViewMode.Theater:
                    nextViewMode = DeviationViewMode.Theater;
                    break;
                default:
            }
            break;
        default:
    }
    return nextViewMode;
}
function* handleRequestChangeViewMode() {
    // Because this waits in a while loop, we dont want it on the serverside
    if (typeof window === 'undefined') {
        return;
    }
    // Things are done in a bit special way here - instead of relying on
    // takeEvery/takeLatest we roll our own event loop. This is because we
    // need to handle browser fullscreen events in different ways depending
    // on where we are in the process of changing view mode and the built in
    // functions were not adequate for this case.
    // If the user requested a change to/from fullscreen, we wait for these
    // events before changing the store to make sure by the time the selector
    // is run (which defaults to the browser's state in case of doubt), the
    // browser is in the right state already.
    // When there is no request from the user, we still listen for fullscreen
    // change events to make absolutely sure we are in sync with whatever the
    // browser is doing (eg. if the user exited fullscreen via esc key)
    // This is because not synchronizing on them can cause race conditions
    // between handling browser fullscreen change and the user's own change
    // request.
    while(true){
        // we wait for either of 3 events here: user-requested mode changes,
        // and the browser fullscreen state changes (enter/exit)
        const { requestChange, fsExit, fsEnter } = yield race({
            requestChange: take(requestChangeViewMode),
            fsExit: take(fullscreenExited),
            fsEnter: take(fullscreenEntered)
        });
        // determine what the target view mode is based on what we got
        const requestedViewMode = requestChange ? requestChange.payload.viewMode : fsExit ? DeviationViewMode.Normal : fsEnter ? DeviationViewMode.Fullscreen : undefined;
        const deviation = get(requestChange || fsExit || fsEnter || {}, 'payload.deviation');
        // this should not happen(tm), but just in case
        if (!requestedViewMode) {
            continue;
        }
        fsLogger.log('View mode change requested:', requestedViewMode);
        // now figure out what our _actual_ next mode will be
        const currentViewMode = yield select(getViewMode);
        const nextViewMode = yield call(getNextViewMode, currentViewMode, requestedViewMode, deviation);
        fsLogger.log('Determined target view mode:', nextViewMode);
        // nothing changed, go back to waiting for events
        if (currentViewMode === nextViewMode) {
            continue;
        }
        // for anything involving fullscreen, we need to synchronize on browser events
        // except if the change was triggered by the browser event to begin with
        if (currentViewMode === DeviationViewMode.Fullscreen && !fsExit) {
            fsLogger.log('Full screen exit sync start');
            yield all([
                call(handleExitFullscreen),
                take(fullscreenExited)
            ]);
            fsLogger.log('Full screen exit sync complete');
        } else if (nextViewMode === DeviationViewMode.Fullscreen && !fsEnter) {
            fsLogger.log('Full screen exit sync start');
            yield all([
                call(handleEnterFullscreen),
                take(fullscreenEntered)
            ]);
            fsLogger.log('Full screen enter sync complete');
        }
        // if all is settled, time to change the store and back to listening for new events
        if (nextViewMode && deviation) {
            fsLogger.log('Mode change ready', nextViewMode);
            const { view, publicSession, eventComponent, deviationId } = yield select(getBiEventData, deviation);
            const event = {
                evid: getBiEventTypeForViewChange(currentViewMode, nextViewMode),
                view,
                component: eventComponent,
                deviationid: deviationId,
                viewmode: nextViewMode
            };
            yield all([
                put(setViewMode({
                    currentViewMode,
                    nextViewMode
                })),
                call([
                    biLogger,
                    'log'
                ], publicSession, event)
            ]);
        }
    }
}
function* handleZoomChange({ payload: { deviation }, type }) {
    const { view, publicSession, eventComponent, deviationId, deviationResourceType } = yield select(getBiEventData, deviation);
    const event = {
        evid: BiEventType.ZOOM_MODE,
        view,
        component: eventComponent,
        typeid: deviationResourceType,
        deviationid: deviationId,
        viewmode: type === enterZoomMode.toString() ? 'zoom' : 'regular'
    };
    yield call([
        biLogger,
        'log'
    ], publicSession, event);
}
function handleExitDuperbrowse() {
    if (fscreen.fullscreenElement) {
        fscreen.exitFullscreen();
    }
}
function* handleCollectFave({ payload: { notificationQueue, actionBarType } }) {
    if (!notificationQueue.startsWith('deviation')) {
        return;
    }
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.ADD_TO_GROUP
        }));
        return;
    }
    yield put(pushPopover(actionBarType === 'primary' ? PopoverType.ADD_TO_COLLECTION_PRIMARY : PopoverType.ADD_TO_COLLECTION_SECONDARY, {
        params: {
            ignorePreselectedCollections: true
        }
    }));
}
function* handleRequestCollectionPanelClose() {
    yield put(hidePanel('collection'));
}
function* handleExpandAndFocusCommentPanel({ payload: { deviation, actionBarType } }) {
    const isMobile = yield select(getIsMobile);
    const { deviationId, typeId } = deviation;
    if (!isMobile) {
        yield put(togglePanel('comment', actionBarType));
        return;
    }
    const editorId = getCommentBoxId(getCommentableTypeForDeviationType(typeId), deviationId, 'top');
    yield put(focusEditorWithScroll(editorId, true));
}
export default function* deviationPageSaga() {
    yield all([
        fork(handleRequestChangeViewMode),
        takeLatest(loadDeviation, handleLoadDeviation),
        takeEvery(initDeviationPage, handleInitDeviationPage),
        takeEvery(unloadDuperbrowseItem, handleUnloadDuperbrowseItem),
        takeLatest(collectFave, handleCollectFave),
        takeEvery(togglePanel, handleTogglePanel),
        takeEvery([
            enterZoomMode,
            exitZoomMode
        ], handleZoomChange),
        takeEvery([
            exitDuperbrowse,
            shutdownDuperbrowse
        ], handleExitDuperbrowse),
        takeEvery(collectionActions.requestClose, handleRequestCollectionPanelClose),
        takeEvery(expandAndFocusCommentPanel, handleExpandAndFocusCommentPanel)
    ]);
}
